import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom"; 
import {
  LayoutDashboard,
  BriefcaseBusiness,
  Users,
  UserCog,
  Settings,
  ChevronDown,
  ChevronRight,
  Circle,
  LogOut,
  Menu,
  Shield,
  Building2, 
  MessageSquare,
  Handshake,
  Video,
  CheckCircle,
  Image // 🌟 NAYA: Banner ke liye icon add kiya
} from "lucide-react";

export default function Sidebar() {
  const [openMenu, setOpenMenu] = useState(""); 
  const [isCollapsed, setIsCollapsed] = useState(false); 
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = (menuName) => {
    if (isCollapsed) setIsCollapsed(false);
    setOpenMenu(openMenu === menuName ? "" : menuName);
  };

  const handleLogout = () => {
    const isConfirmed = window.confirm("Are you sure you want to logout?");
    if (isConfirmed) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin");
      navigate("/admin/login");
    }
  };

  const menuItems = [
    { title: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { title: "Jobs", icon: BriefcaseBusiness, subItems: [
        { title: "Add New Job", path: "/jobs/new" },
        { title: "Job Listing", path: "/jobs" },
        { title: "Active Jobs", path: "/jobs/active" },
        { title: "Expired Jobs", path: "/jobs/expired" },
        { title: "Job Categories", path: "/categories-list" }, 
      ]
    },
    { title: "Candidates", icon: Users, subItems: [
        { title: "Add New Candidate", path: "/candidates/new" },
        { title: "All Candidates", path: "/candidates" },
        { title: "Active Candidates", path: "/candidates/active" },
        { title: "Shortlisted Candidates", path: "/candidates/shortlisted" },
        { title: "Rejected Candidates", path: "/candidates/rejected" },
      ]
    },
    { title: "Top Companies", icon: Building2, path: "/admin/top-companies" },
    { title: "Testimonials", icon: MessageSquare, path: "/admin/testimonials" },
    { title: "Manage Partners", icon: Handshake, path: "/admin/partners" },
    { title: "Employers", icon: Building2, path: "/admin/employers" },
    { title: "Recharge Requests", icon: CheckCircle, path: "/admin/payment-approvals" },
    // 🌟 NAYA: Hero Banner Management ka menu item add kiya
    { title: "Hero Banner", icon: Image, path: "/admin/banner-manager" },
    { title: "Manage Videos", icon: Video, subItems: [
        { title: "Add Video", path: "/videos/new" },
        { title: "Video List", path: "/videos" },
      ]
    },
    { title: "Employees", icon: UserCog, subItems: [
        { title: "Add New Employee", path: "/employees/new" },
        { title: "Employee List", path: "/employees" },
      ]
    },
    { title: "Roles & Permissions", icon: Shield, path: "/roles" }, 
    { title: "Settings", icon: Settings, path: "/settings" },
  ];

  // --- SAFE PARSING LOGIC START ---
  let adminData = {};
  try {
    const rawData = localStorage.getItem("admin");
    if (rawData && rawData !== "undefined" && rawData !== "null") {
      adminData = JSON.parse(rawData);
    }
  } catch (e) {
    console.warn("Sidebar admin data corrupted, resetting to empty:", e);
    adminData = {};
  }
  
  let userPermissions = {};
  try {
    const rawPerms = adminData.permissions || (adminData.role && adminData.role.permissions);

    const parsedPerms = typeof rawPerms === 'string' 
      ? JSON.parse(rawPerms) 
      : (rawPerms || {});
    
    if (Array.isArray(parsedPerms)) {
      parsedPerms.forEach(p => {
        let key = p.replace("manage_", "");
        userPermissions[key] = ["all"]; 
      });
    } else {
      userPermissions = parsedPerms;
    }
  } catch(e) {
    console.warn("Error parsing permissions", e);
    userPermissions = {};
  }
  // --- SAFE PARSING LOGIC END ---

  const userRoleStr = typeof adminData.role === 'string' ? adminData.role.toLowerCase() : ""; 

  const allowedItems = menuItems.filter(item => {
    if (userRoleStr === 'admin' || userRoleStr === 'super admin' || adminData.role_id === 1 || adminData.email === 'admin@kadtech.com') {
        return true;
    }
    
    const moduleMap = {
      'Dashboard': 'dashboard',
      'Jobs': 'jobs',
      'Candidates': 'candidates',
      'Top Companies': 'top_companies',
      'Testimonials': 'testimonials',
      'Manage Partners': 'manage_partners',
      'Employers': 'employers',
      'Recharge Requests': 'payment_approvals',
      'Hero Banner': 'banner_manager', // 🌟 NAYA: Permission key map ki
      'Manage Videos': 'manage_videos',
      'Employees': 'employees',
      'Roles & Permissions': 'roles',
      'Settings': 'settings'
    };

    const permKey = moduleMap[item.title];

    if (permKey && userPermissions[permKey] && userPermissions[permKey].length > 0) {
      return true;
    }

    return false;
  });

  return (
    <div className={`${isCollapsed ? "w-20" : "w-64"} shrink-0 bg-black h-screen text-gray-300 flex flex-col border-r border-gray-800 transition-all duration-300 z-10`}>
      
      <div className={`p-5 flex items-center border-b border-gray-800 ${isCollapsed ? "justify-center" : "justify-between"}`}>
        {!isCollapsed && (
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-white font-bold tracking-widest text-sm whitespace-nowrap">
              KAD TECH <span className="text-yellow-500">HR</span>
            </span>
          </div>
        )}
        <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-1 hover:bg-gray-800 rounded-md text-yellow-500 transition-colors">
          <Menu size={24} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-6 px-3 space-y-2 overflow-x-hidden">
        {allowedItems.map((item, index) => {
          const isActive = location.pathname === item.path || (item.subItems && item.subItems.some(sub => location.pathname === sub.path));
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isOpen = openMenu === item.title;

          return (
            <div key={index}>
              {hasSubItems ? (
                <button
                  onClick={() => toggleMenu(item.title)}
                  className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                    isCollapsed ? "justify-center" : "justify-between"
                  } ${isOpen || isActive ? "bg-gray-900 text-white" : "hover:bg-gray-900 hover:text-white"}`}
                  title={isCollapsed ? item.title : ""}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={20} className={(isOpen || isActive) && !isCollapsed ? "text-yellow-500" : ""} />
                    {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.title}</span>}
                  </div>
                  {!isCollapsed && (isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                </button>
              ) : (
                <Link
                  to={item.path}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isCollapsed ? "justify-center" : ""
                  } ${isActive ? "bg-yellow-400 text-black shadow-[0_0_15px_rgba(250,204,21,0.2)]" : "hover:bg-gray-900 hover:text-white"}`}
                  title={isCollapsed ? item.title : ""}
                >
                  <item.icon size={20} />
                  {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.title}</span>}
                </Link>
              )}

              {hasSubItems && isOpen && !isCollapsed && (
                <div className="mt-1 ml-4 pl-4 border-l border-gray-800 space-y-1">
                  {item.subItems.map((sub, subIndex) => {
                    const isSubActive = location.pathname === sub.path;
                    return (
                      <Link
                        key={subIndex}
                        to={sub.path}
                        className={`flex items-center gap-3 p-2 rounded-lg text-sm transition-colors ${
                          isSubActive ? "text-yellow-500 font-medium" : "text-gray-400 hover:text-white hover:bg-gray-900"
                        }`}
                      >
                        <Circle size={8} className={isSubActive ? "fill-yellow-500" : ""} />
                        <span className="whitespace-nowrap">{sub.title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t border-gray-800">
        <button
          onClick={handleLogout}
          className={`w-full flex items-center p-3 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-500 transition-colors ${
            isCollapsed ? "justify-center" : "gap-3"
          }`}
          title={isCollapsed ? "Logout" : ""}
        >
          <LogOut size={20} />
          {!isCollapsed && <span className="font-medium whitespace-nowrap">Logout</span>}
        </button>
      </div>
    </div>
  );
}